/**
 * @file components/RegionDetail.tsx
 * @description SEO-optimized regional page component
 * Displays bikeparks from a specific region with map, stats, ranking, and comparison table
 */

'use client';

import { useState } from 'react';
import { Bikepark } from '@/types';
import { Region, RegionTranslation } from '@/config/regions';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useTranslations } from 'next-intl';

// Dynamically import MapView to avoid SSR issues
const MapView = dynamic(() => import('@/components/MapView'), { ssr: false });

interface RegionDetailProps {
  region: Region;
  bikeparks: Bikepark[];
  translation: RegionTranslation;
  locale: string;
}

export default function RegionDetail({ region, bikeparks, translation, locale }: RegionDetailProps) {
  const t = useTranslations('regionPage');
  const [showMap, setShowMap] = useState(false);

  // Calculate statistics
  const stats = {
    count: bikeparks.length,
    avgPrice: bikeparks
      .filter(bp => bp.price && bp.price > 0)
      .reduce((sum, bp) => sum + (bp.price || 0), 0) / bikeparks.filter(bp => bp.price).length || 0,
    avgRating: bikeparks.reduce((sum, bp) => sum + bp.rating, 0) / bikeparks.length || 0,
    withLift: bikeparks.filter(bp => bp.lift).length,
    withSchool: bikeparks.filter(bp => bp.school).length,
    trailsCount: bikeparks.reduce((sum, bp) => sum + (bp.trailsCount || 0), 0),
  };

  const minPrice = Math.min(...bikeparks.filter(bp => bp.price && bp.price > 0).map(bp => bp.price || 0));
  const maxPrice = Math.max(...bikeparks.filter(bp => bp.price && bp.price > 0).map(bp => bp.price || 0));

  return (
    <div className="region-detail">
      {/* Hero Section */}
      <header className="region-hero">
        <div className="hero-background">
          <div className="hero-gradient"></div>
          <div className="hero-pattern"></div>
        </div>
        <div className="hero-content">
          {/* Breadcrumbs for SEO */}
          <nav className="hero-breadcrumb" aria-label="breadcrumb">
            <ol itemScope itemType="https://schema.org/BreadcrumbList">
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <Link href="/" itemProp="item">
                  <span itemProp="name">BikeparkMapa.pl</span>
                </Link>
                <meta itemProp="position" content="1" />
              </li>
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <Link href="/regiony" itemProp="item">
                  <span itemProp="name">Regiony</span>
                </Link>
                <meta itemProp="position" content="2" />
              </li>
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem" className="active">
                <span itemProp="name">{translation.name}</span>
                <meta itemProp="position" content="3" />
              </li>
            </ol>
          </nav>

          <div className="hero-badge">
            🏔️ {stats.count} {stats.count === 1 ? t('bikeparkSingular') : stats.count < 5 ? t('bikeparks2to4') : t('bikeparks5plus')}
          </div>

          <h1 className="hero-title">{t('heroTitle', { region: region.nameLocative })}</h1>

          <p className="hero-lead">
            {t('heroLead', {
              count: stats.count,
              bikeparks: stats.count === 1 ? t('bikeparkSingular').toLowerCase() : stats.count < 5 ? t('bikeparks2to4').toLowerCase() : t('bikeparks5plus').toLowerCase(),
              region: region.name
            })}
          </p>

          <div className="hero-stats">
            <div className="hero-stat">
              <div className="stat-number">{stats.count}</div>
              <div className="stat-label">{t('statBikeparks')}</div>
            </div>
            <div className="hero-stat">
              <div className="stat-number">{stats.trailsCount || '15+' }</div>
              <div className="stat-label">{t('statTrails')}</div>
            </div>
            <div className="hero-stat">
              <div className="stat-number">⭐ {stats.avgRating.toFixed(1)}</div>
              <div className="stat-label">{t('statAvgRating')}</div>
            </div>
          </div>
        </div>
      </header>

      {/* Key Statistics */}
      <section className="region-stats">
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-number">{stats.count}</div>
            <div className="stat-label">{t('statBikeparks')}</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.trailsCount}+</div>
            <div className="stat-label">{t('statTrails')}</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{Math.round(stats.avgPrice)} zł</div>
            <div className="stat-label">{t('statAvgPrice')}</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">⭐ {stats.avgRating.toFixed(1)}</div>
            <div className="stat-label">{t('statAvgRating')}</div>
          </div>
        </div>
      </section>

      {/* Region Description - SEO Content */}
      <section className="region-description">
        <h2>{t('descriptionTitle', { region: region.nameLocative, type: stats.count === 1 ? t('bestParkSingular') : t('bestParksPlural') })}</h2>
        <div className="description-content" dangerouslySetInnerHTML={{ __html: translation.description.replace(/\n/g, '</p><p>') }} />
      </section>

      {/* Interactive Map */}
      <section className="region-map-section">
        <h2>{t('mapTitle', { region: region.nameLocative })}</h2>
        <button
          className="show-map-button"
          onClick={() => setShowMap(!showMap)}
        >
          {showMap ? t('hideMap') : t('showMap')}
        </button>
        {showMap && (
          <div className="map-container">
            <MapView
              parks={bikeparks}
              type="bikepark"
              center={region.coordinates}
              zoom={9}
            />
          </div>
        )}
      </section>

      {/* Bikeparks Ranking */}
      <section className="region-ranking">
        <h2>{t('rankingTitle', { region: region.nameLocative })}</h2>
        <p className="ranking-subtitle">
          {t('rankingSubtitle', { count: stats.count })}
        </p>

        <div className="bikeparks-list">
          {bikeparks.map((park, index) => (
            <article key={park.id} className="bikepark-card" itemScope itemType="https://schema.org/TouristAttraction">
              <div className="card-rank">#{index + 1}</div>

              <div className="card-content">
                <h3 itemProp="name">
                  <Link href={`/bikepark/${park.slug}`}>{park.name}</Link>
                </h3>

                <div className="card-meta">
                  <span className="rating" itemProp="aggregateRating" itemScope itemType="https://schema.org/AggregateRating">
                    ⭐ <span itemProp="ratingValue">{park.rating}</span><span itemProp="bestRating" style={{display: 'none'}}>5</span>/5
                  </span>
                  {park.price && park.price > 0 && (
                    <span className="price">💰 {park.price} zł/dzień</span>
                  )}
                  {!park.price && park.priceInfo && (
                    <span className="price">💰 {park.priceInfo}</span>
                  )}
                </div>

                <p className="card-description" itemProp="description">{park.blurb}</p>

                <div className="card-features">
                  {park.lift && <span className="badge">✓ {t('featureLift')}</span>}
                  {park.bikeService && <span className="badge">✓ {t('featureService')}</span>}
                  {park.school && <span className="badge">✓ {t('featureSchool')}</span>}
                  {park.restaurant && <span className="badge">✓ {t('featureRestaurant')}</span>}
                  {park.accommodation && <span className="badge">✓ {t('featureAccommodation')}</span>}
                  {park.familyFriendly && <span className="badge">👨‍👩‍👧 {t('featureFamily')}</span>}
                </div>

                <Link href={`/bikepark/${park.slug}`} className="card-cta">
                  {t('viewDetails')} →
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Price Comparison Table - SEO Gold */}
      <section className="region-comparison">
        <h2>{t('comparisonTitle', { region: region.nameLocative })}</h2>
        <p>
          {t('comparisonIntro', {
            region: region.nameGenitive,
            minPrice: Math.round(minPrice),
            maxPrice: Math.round(maxPrice)
          })}
        </p>

        <div className="comparison-table-wrapper">
          <table className="comparison-table">
            <thead>
              <tr>
                <th>{t('tableHeaderName')}</th>
                <th>{t('tableHeaderPrice')}</th>
                <th>{t('tableHeaderLift')}</th>
                <th>{t('tableHeaderRating')}</th>
              </tr>
            </thead>
            <tbody>
              {bikeparks.map(park => (
                <tr key={park.id}>
                  <td>
                    <Link href={`/bikepark/${park.slug}`}>{park.name}</Link>
                  </td>
                  <td>
                    {park.price && park.price > 0 ? `${park.price} zł` : (park.priceInfo || t('priceCheckOnSite'))}
                  </td>
                  <td>{park.lift ? `✅ ${t('yes')}` : `❌ ${t('no')}`}</td>
                  <td>⭐ {park.rating}/5</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* FAQ Section with Schema */}
      <section className="region-faq">
        <h2>{t('faqTitle')}</h2>

        <div itemScope itemType="https://schema.org/FAQPage">
          <div itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
            <h3 itemProp="name">{t('faqCountQuestion', { region: region.nameLocative })}</h3>
            <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
              <p itemProp="text">
                {t('faqCountAnswer', {
                  region: region.name,
                  count: stats.count,
                  topParks: stats.count > 3 ? ` ${t('faqTopParks')}: ${bikeparks.slice(0, 3).map(bp => bp.name).join(', ')}.` : ''
                })}
              </p>
            </div>
          </div>

          <div itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
            <h3 itemProp="name">{t('faqBestQuestion', { region: region.nameLocative })}</h3>
            <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
              <p itemProp="text">
                {t('faqBestAnswer', {
                  region: region.nameLocative,
                  name: bikeparks[0]?.name,
                  rating: bikeparks[0]?.rating
                })}
              </p>
            </div>
          </div>

          <div itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
            <h3 itemProp="name">{t('faqPriceQuestion', { region: region.nameGenitive })}</h3>
            <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
              <p itemProp="text">
                {t('faqPriceAnswer', {
                  region: region.nameLocative,
                  avgPrice: Math.round(stats.avgPrice),
                  cheapestName: bikeparks.find(bp => bp.price === minPrice)?.name,
                  minPrice: Math.round(minPrice),
                  expensiveName: bikeparks.find(bp => bp.price === maxPrice)?.name,
                  maxPrice: Math.round(maxPrice)
                })}
              </p>
            </div>
          </div>

          {stats.withLift > 0 && (
            <div itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
              <h3 itemProp="name">{t('faqLiftQuestion', { region: region.nameLocative })}</h3>
              <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                <p itemProp="text">
                  {t('faqLiftAnswer', {
                    region: region.nameLocative,
                    count: stats.withLift,
                    bikeparks: stats.withLift === 1 ? t('bikeparkSingular').toLowerCase() : stats.withLift < 5 ? t('bikeparks2to4').toLowerCase() : t('bikeparks5plus').toLowerCase(),
                    names: bikeparks.filter(bp => bp.lift).map(bp => bp.name).join(', ')
                  })}
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Related Content - Internal Linking */}
      <section className="region-related">
        <h2>{t('relatedTitle')}</h2>
        <ul className="related-links">
          <li><Link href="/bikeparki/lista">{t('relatedAllBikeparks')}</Link></li>
          <li><Link href="/bikeparki/ranking">{t('relatedRanking')}</Link></li>
          <li><Link href="/porownanie-cen">{t('relatedPriceComparison')}</Link></li>
          <li><Link href="/przewodnik">{t('relatedGuide')}</Link></li>
          <li><Link href="/regiony">{t('relatedRegions')}</Link></li>
        </ul>
      </section>

      {/* Structured Data - JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `Bikeparki w ${region.nameLocative}`,
            description: translation.description.substring(0, 200),
            url: `https://bikeparkmapa.pl/region/${region.slug}`,
            about: {
              '@type': 'Place',
              name: region.name,
              geo: {
                '@type': 'GeoCoordinates',
                latitude: region.coordinates.lat,
                longitude: region.coordinates.lng,
              },
            },
            hasPart: bikeparks.map(bp => ({
              '@type': 'TouristAttraction',
              name: bp.name,
              url: `https://bikeparkmapa.pl/bikepark/${bp.slug}`,
              aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: bp.rating,
                bestRating: 5,
              },
            })),
          }),
        }}
      />
    </div>
  );
}
